import React, { useEffect } from 'react'
import { Box, Tab, Tabs, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import Attributes from './Attributes'
import Relations from './Relations'
import BusinessRules from './BusinessRules'
import Workflows from './Workflows'
import { useAppSelector } from '../../redux/hook'
import Settings from './Settings'
import { useTranslation } from 'react-i18next'
import { UserPermissions } from '../../constants/constants'
import { usePermissions } from '../../hooks/usePermissions'

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

const CustomTabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            tabIndex={0}
            {...other}
            style={{
                borderRight: '1px solid rgba(0, 0, 0, 0.05)',
                borderLeft: '1px solid rgba(0, 0, 0, 0.05)',
                borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                borderColor: 'divider',
            }}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    )
}

const a11yProps = (index: number) => {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    }
}

const CustomTab = styled((props: any) => <Tab disableRipple {...props} />)(
    ({ theme }) => ({
        textTransform: 'none',
        minWidth: 0,
        [theme.breakpoints.up('sm')]: {
            minWidth: 0,
        },
        fontWeight: theme.typography.fontWeightRegular,
        marginRight: theme.spacing(1),
        '&.Mui-selected': {
            fontWeight: theme.typography.fontWeightMedium,
            color: '#00797a',
        },
        '&.Mui-focusVisible': {
            backgroundColor: 'rgba(100, 95, 228, 0.32)',
        },
        paddingLeft: '0px',
        paddingRight: '0px',
        marginLeft: '15px',
        // height: '100%',
        // marginRight: '5px',
    }),
)

const CustomTabs = styled((props: any) => <Tabs {...props} />)(({ theme }) => ({
    '& .MuiTabs-indicator': {
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: 'transparent',
        '& > span': {
            maxWidth: '100%',
            width: '100%',
            backgroundColor: theme.palette.primary.main,
        },
    },
}))

const DTRightPane: React.FC = () => {
    const [value, setValue] = React.useState(0)
    const selectedEntity = useAppSelector(
        (state) => state.dtStudio.selectedEntity,
    )
    const name = selectedEntity?.name
    const { t } = useTranslation()
    const { hasPermission } = usePermissions()

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue)
    }

    useEffect(() => {
        setValue(0)
    }, [selectedEntity])

    if (!name) {
        /**
         * Check if this needs to be a Spinner instead of null
         */
        return null
    }

    return (
        <Box
            sx={{ p: 3, height: '100%', overflow: 'auto', flex: '1 1 auto' }}
            role="region"
            aria-label={t('configurationPanel')}
        >
            <Typography variant="h3">Configure {name}</Typography>
            <Box
                sx={{
                    width: '100%',
                    maxHeight: '100%',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    mt: 3,
                }}
            >
                <CustomTabs
                    value={value}
                    onChange={handleChange}
                    aria-label={t('configurationPanel')}
                    TabIndicatorProps={{
                        children: <span />,
                    }}
                >
                    <CustomTab
                        label={t('attributes')}
                        title={t('attributes') || 'Attributes'}
                        {...a11yProps(0)}
                    />
                    <CustomTab
                        label={t('relations')}
                        title={t('relations') || 'Relations'}
                        {...a11yProps(1)}
                    />
                    <CustomTab
                        label={t('businessRules')}
                        title={t('businessRules') || 'Business Rules'}
                        {...a11yProps(2)}
                    />
                    <CustomTab
                        label={t('workflow')}
                        title={t('workflow') || 'Workflow'}
                        {...a11yProps(3)}
                    />
                    {selectedEntity.ownTemplate &&
                        hasPermission(UserPermissions.ENTITY_WRITER) && (
                            <CustomTab
                                label={t('settings')}
                                title={t('settings') || 'Settings'}
                                {...a11yProps(4)}
                            />
                        )}
                </CustomTabs>
            </Box>
            <CustomTabPanel value={value} index={0}>
                <Attributes />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
                <Relations />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2}>
                <BusinessRules maxHeight="calc(100vh - 440px)" />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={3}>
                <Workflows />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={4}>
                <Settings />
            </CustomTabPanel>
        </Box>
    )
}

export default DTRightPane
